import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileExcel, FaEye, FaExclamationTriangle, FaCheckCircle, FaDownload, FaSyncAlt, FaFileCsv, FaFilePdf, FaSearch, FaChevronDown } from 'react-icons/fa';
import Modal from '../../../components/Utils/ModalShow';
import { apiService } from '../../../components/Config/api.service';
import { notify } from '../../../components/Utils/Notifications';

const AccountingJournal = ({ isOpen = true, onClose, selectedMonth, isInline = false }) => {
    const { t } = useTranslation();
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportDone, setExportDone] = useState(false);
    const [exportFormat, setExportFormat] = useState('detail'); // 'detail' or 'grouped'
    const [fileFormat, setFileFormat] = useState('xlsx'); // 'xlsx' | 'csv' | 'pdf'
    const [groupedBy, setGroupedBy] = useState('account'); // 'account' | 'department' | 'assignment'
    const [includeAccountBreakdown, setIncludeAccountBreakdown] = useState(false);
    const [detailDeductions, setDetailDeductions] = useState(false);
    const [exportTemplate, setExportTemplate] = useState('yros');
    const [downloadFormat, setDownloadFormat] = useState('xlsx');

    // Preview state
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    // Options appliquées à l'aperçu affiché : tant qu'elles diffèrent des options
    // choisies, l'aperçu est signalé comme à actualiser (pas de rechargement automatique).
    const [appliedOptions, setAppliedOptions] = useState(null);
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);
    // Recherche, filtre et pagination du tableau : affichage seulement.
    const [tableSearch, setTableSearch] = useState('');
    const [onlyMissingAccount, setOnlyMissingAccount] = useState(false);
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 50;

    const handleCloseProgressModal = () => {
        setShowProgressModal(false);
        setExportDone(false);
    };

    const getPayload = useCallback((fmt = fileFormat, template = exportTemplate) => {
        let gb = groupedBy;
        if (groupedBy === 'department' && includeAccountBreakdown) gb = 'account_department';
        if (groupedBy === 'assignment' && includeAccountBreakdown) gb = 'account_assignment';

        const include_totals = !includeAccountBreakdown;

        return {
            month: selectedMonth,
            generate_xlsx: fmt === 'xlsx',
            file_format: fmt,
            grouped: exportFormat === 'grouped',
            grouped_by: gb,
            include_totals: include_totals,
            detail_deductions: detailDeductions,
            export_template: template
        };
    }, [selectedMonth, fileFormat, exportFormat, groupedBy, includeAccountBreakdown, detailDeductions, exportTemplate]);

    const handleFetchPreview = useCallback(async () => {
        if (!isInline && !isOpen) return;
        setIsPreviewLoading(true);
        setPreviewError(null);
        try {
            const payload = getPayload('preview');
            const res = await apiService.saveElement('/accounting/export', payload);
            const data = (res && res.data && (res.data.result || res.data)) ? (res.data.result || res.data) : null;

            if (data && data.status === 'error') {
                throw new Error(data.message || t('accountingJournal.exportError'));
            }

            setPreviewData(data);
            setAppliedOptions(JSON.stringify(payload));
            setPage(1);
        } catch (e) {
            console.error('Erreur lors de la prévisualisation du journal comptable:', e);
            if (e.response?.status === 403) {
                notify(t('employeeDetails.insufficientRights'), "top-right", 3);
            } else {
                notify(t('employeeDetails.serverError'), "top-right", 3);
            }
            setPreviewError(e.message || t('accountingJournal.exportErrorMessage'));
        } finally {
            setIsPreviewLoading(false);
        }
    }, [isOpen, isInline, getPayload, t]);

    // L'aperçu se charge à l'ouverture et au changement de mois. Un changement
    // d'option ne recharge rien : c'est le bouton « Actualiser » qui l'applique.
    const fetchPreviewRef = useRef(handleFetchPreview);
    fetchPreviewRef.current = handleFetchPreview;
    useEffect(() => {
        if (isInline || isOpen) {
            fetchPreviewRef.current();
        }
    }, [isOpen, isInline, selectedMonth]);

    const isStale = appliedOptions !== null && appliedOptions !== JSON.stringify(getPayload('preview'));

    // template : force un modèle d'export, quel que soit celui sélectionné (bouton « CSV (Sage) »).
    const handleDownloadJournal = async (targetFormat = fileFormat, template = exportTemplate) => {
        setShowDownloadMenu(false);
        if (!isInline && onClose) onClose();
        setDownloadFormat(targetFormat);
        setShowProgressModal(true);
        setIsExporting(true);
        setExportDone(false);
        try {
            const payload = getPayload(targetFormat, template);
            const res = await apiService.saveElement('/accounting/export', payload);
            const data = (res && res.data && (res.data.result || res.data)) ? (res.data.result || res.data) : null;

            if (data && data.status === 'error') {
                throw new Error(data.message || t('accountingJournal.exportError'));
            }

            const fileBase64 = data && (data.file || data.base64 || (data.result && data.result.file));
            const contentType = (data && data.content_type) || {
                xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                csv: "text/csv;charset=utf-8",
                pdf: "application/pdf",
            }[targetFormat];

            let gb = groupedBy;
            if (groupedBy === 'department' && includeAccountBreakdown) gb = 'account_department';
            if (groupedBy === 'assignment' && includeAccountBreakdown) gb = 'account_assignment';
            const filename = (data && (data.filename || data.name)) || `Journal_Comptable_${selectedMonth}_${gb}.${targetFormat}`;

            if (!fileBase64) throw new Error(t('accountingJournal.fileNotReceived'));

            const byteCharacters = atob(fileBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: contentType });
            apiService.downloadBlob(blob, filename);
            setExportDone(true);
        } catch (e) {
            setExportDone(false);
            if (e.response?.status === 403) {
                notify(t('employeeDetails.insufficientRights'), "top-right", 3);
            } else {
                notify(t('employeeDetails.serverError'), "top-right", 3);
            }
            console.error('Erreur lors de la récupération du journal comptable:', e);
        }
        setIsExporting(false);
    };

    const headers = previewData?.headers || [];
    const rows = previewData?.rows || [];
    const accountIdx = headers.findIndex(h => ['compte', 'n° de compte'].includes(String(h).toLowerCase()));
    const debitIdx = headers.findIndex(h => /d[ée]bit/i.test(String(h)));
    const creditIdx = headers.findIndex(h => /cr[ée]dit/i.test(String(h)));
    // Colonnes du salarié : absentes des vues regroupées, où la recherche par salarié n'a pas de sens.
    const employeeIdx = headers.findIndex(h => /collaborateur|salari|employ/i.test(String(h)));
    const matriculeIdx = headers.findIndex(h => /matricule/i.test(String(h)));
    const hasEmployeeColumns = employeeIdx >= 0 || matriculeIdx >= 0;
    const currency = previewData?.currency || 'FCFA';
    const formatAmount = (value) => Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 });
    const isTotalRow = (row) => String(row?.[0] || '').startsWith('Total ');
    const normalize = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const employeeTerm = hasEmployeeColumns ? employeeSearch.trim() : '';
    const isFilteringTable = Boolean(tableSearch.trim()) || onlyMissingAccount || Boolean(employeeTerm);
    const filteredRows = useMemo(() => {
        if (!isFilteringTable) return rows;
        const term = normalize(tableSearch.trim());
        const employee = normalize(employeeTerm);
        // Les lignes de sous-total n'ont plus de sens sur une sélection : on les retire.
        return rows.filter((row) => {
            if (isTotalRow(row)) return false;
            if (onlyMissingAccount && String(row[accountIdx] || '').trim()) return false;
            // Salarié ou matricule : uniquement dans ces deux colonnes, pas dans les libellés.
            if (employee && ![employeeIdx, matriculeIdx].some((idx) => idx >= 0 && normalize(row[idx]).includes(employee))) return false;
            return !term || row.some((cell) => normalize(cell).includes(term));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, tableSearch, onlyMissingAccount, accountIdx, employeeTerm, employeeIdx, matriculeIdx]);
    // Suggestions : « Nom (matricule) » de chaque salarié présent dans le journal.
    const employeeOptions = useMemo(() => {
        if (!hasEmployeeColumns) return [];
        const seen = new Map();
        rows.forEach((row) => {
            if (isTotalRow(row)) return;
            const name = employeeIdx >= 0 ? String(row[employeeIdx] || '').trim() : '';
            const matricule = matriculeIdx >= 0 ? String(row[matriculeIdx] || '').trim() : '';
            if (name || matricule) seen.set(`${name}|${matricule}`, { name, matricule });
        });
        return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, employeeIdx, matriculeIdx]);
    const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const pageRows = filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const sumColumn = (idx) => (idx < 0 ? 0 : filteredRows.reduce((sum, row) => (isTotalRow(row) ? sum : sum + (Number(row[idx]) || 0)), 0));
    const filteredDebit = sumColumn(debitIdx);
    const filteredCredit = sumColumn(creditIdx);

    const pendingCount = previewData?.pending_payslip_count || 0;
    const closedCount = previewData?.closed_move_count || 0;
    const monthStatus = pendingCount > 0 ? (closedCount > 0 ? 'partial' : 'provisional') : (closedCount > 0 ? 'final' : null);

    const downloadChoices = [
        { id: 'xlsx', label: t('accountingJournal.downloadExcel'), icon: <FaFileExcel className="text-emerald-600" size={13} />, run: () => handleDownloadJournal('xlsx') },
        { id: 'csv', label: t('accountingJournal.downloadCsv'), icon: <FaFileCsv className="text-blue-600" size={13} />, run: () => handleDownloadJournal('csv') },
        { id: 'csv-sage', label: t('accountingJournal.downloadCsvSage'), icon: <FaFileCsv className="text-blue-600" size={13} />, run: () => handleDownloadJournal('csv', 'sage') },
        { id: 'pdf', label: t('accountingJournal.downloadPdf'), icon: <FaFilePdf className="text-rose-600" size={13} />, run: () => handleDownloadJournal('pdf') },
    ];

    const renderContent = () => (
        <div className="flex flex-col space-y-4 w-full">
            {/* Options d'affichage et d'export : repliées par défaut */}
            <details className="bg-gray-50 rounded-lg border shadow-sm" data-cy="journal-options">
            <summary className="cursor-pointer px-4 py-2.5 text-xs font-semibold text-gray-700 flex items-center justify-between">
                <span>{t('accountingJournal.optionsTitle')}</span>
                {isStale && (
                    <span className="text-amber-700 font-medium" data-cy="journal-stale">{t('accountingJournal.optionsChanged')}</span>
                )}
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border-t">
                <div>
                    <label className="block text-xs font-semibold mb-1.5 text-gray-700">{t('accountingJournal.dataOrganization')}</label>
                    <div className="flex gap-1 bg-white p-1 rounded border">
                        <button
                            className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-all ${exportFormat === 'detail' ? 'bg-[--color-primary] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setExportFormat('detail')}
                        >
                            {t('accountingJournal.detailedByEmployee')}
                        </button>
                        <button
                            className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-all ${exportFormat === 'grouped' ? 'bg-[--color-primary] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setExportFormat('grouped')}
                        >
                            {t('accountingJournal.grouped')}
                        </button>
                    </div>

                    {exportFormat === 'grouped' && (
                        <div className="mt-2.5">
                            <label className="block text-[11px] font-medium mb-1 text-gray-600">{t('accountingJournal.groupBy')}</label>
                            <div className="flex gap-1.5">
                                <button
                                    className={`px-2.5 py-1 rounded text-xs ${groupedBy === 'account' ? 'bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200' : 'bg-white border text-gray-600'}`}
                                    onClick={() => { setGroupedBy('account'); setIncludeAccountBreakdown(false); }}
                                >
                                    {t('accountingJournal.account')}
                                </button>
                                <button
                                    className={`px-2.5 py-1 rounded text-xs ${groupedBy === 'department' ? 'bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200' : 'bg-white border text-gray-600'}`}
                                    onClick={() => { setGroupedBy('department'); setDetailDeductions(false); }}
                                >
                                    {t('accountingJournal.department')}
                                </button>
                                <button
                                    className={`px-2.5 py-1 rounded text-xs ${groupedBy === 'assignment' ? 'bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200' : 'bg-white border text-gray-600'}`}
                                    onClick={() => { setGroupedBy('assignment'); setDetailDeductions(false); }}
                                >
                                    {t('accountingJournal.assignment')}
                                </button>
                            </div>
                            {(groupedBy === 'department' || groupedBy === 'assignment') && (
                                <div className="mt-2 flex items-center gap-1.5">
                                    <input
                                        id="includeAccountBreakdown"
                                        type="checkbox"
                                        checked={includeAccountBreakdown}
                                        onChange={(e) => setIncludeAccountBreakdown(e.target.checked)}
                                    />
                                    <label htmlFor="includeAccountBreakdown" className="text-[11px] text-gray-700 cursor-pointer">
                                        {t('accountingJournal.includeAccountBreakdown')}
                                    </label>
                                </div>
                            )}
                            {groupedBy === 'account' && (
                            <div className="mt-1.5 flex items-center gap-1.5">
                                <input
                                    id="detailDeductions"
                                    type="checkbox"
                                    checked={detailDeductions}
                                    onChange={(e) => setDetailDeductions(e.target.checked)}
                                />
                                <label htmlFor="detailDeductions" className="text-[11px] text-gray-700 cursor-pointer">
                                    {t('accountingJournal.detailDeductions')}
                                </label>
                            </div>
                            )}
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-xs font-semibold mb-1.5 text-gray-700">{t('accountingJournal.exportTemplate')}</label>
                    <div className="flex gap-1 bg-white p-1 rounded border">
                        <button
                            className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-all ${exportTemplate === 'yros' ? 'bg-[--color-primary] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setExportTemplate('yros')}
                        >
                            {t('accountingJournal.standardYros')}
                        </button>
                        <button
                            className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-all ${exportTemplate === 'sage' ? 'bg-[--color-primary] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setExportTemplate('sage')}
                        >
                            {t('accountingJournal.sageOdoo')}
                        </button>
                    </div>
                </div>

                {!isInline && (
                <div>
                    <label className="block text-xs font-semibold mb-1.5 text-gray-700">{t('accountingJournal.fileFormat')}</label>
                    <div className="grid grid-cols-3 gap-1 bg-white p-1 rounded border">
                        <button
                            className={`py-1.5 px-2 rounded text-xs font-medium transition-all ${fileFormat === 'xlsx' ? 'bg-[--color-primary] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setFileFormat('xlsx')}
                        >
                            Excel
                        </button>
                        <button
                            className={`py-1.5 px-2 rounded text-xs font-medium transition-all ${fileFormat === 'csv' ? 'bg-[--color-primary] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setFileFormat('csv')}
                        >
                            CSV
                        </button>
                        <button
                            className={`py-1.5 px-2 rounded text-xs font-medium transition-all ${fileFormat === 'pdf' ? 'bg-[--color-primary] text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                            onClick={() => setFileFormat('pdf')}
                        >
                            PDF
                        </button>
                    </div>
                </div>
                )}
            </div>
            </details>

            {/* Header bar avec Statut et Totaux */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-3.5 rounded-lg border shadow-sm gap-3">
                <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                        <FaEye className="text-[--color-primary]" size={15} />
                        {t('accountingJournal.entriesTitle', { month: selectedMonth })}
                    </span>
                    {monthStatus && (
                        <span
                            className={`px-3 py-1 text-xs font-semibold rounded-full border ${monthStatus === 'final' ? 'bg-gray-100 text-gray-800 border-gray-300' : 'bg-blue-100 text-blue-800 border-blue-300'}`}
                            data-cy="journal-status"
                        >
                            {t(`accountingJournal.status_${monthStatus}`)}
                        </span>
                    )}
                    {previewData && (
                        previewData.is_balanced ? (
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center gap-1 border border-green-300">
                                <FaCheckCircle className="text-green-600" size={13} />
                                {t('accountingJournal.balanced')}
                            </span>
                        ) : (
                            <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full flex items-center gap-1 border border-red-300">
                                <FaExclamationTriangle className="text-red-600" size={13} />
                                {t('accountingJournal.unbalanced', {
                                    amount: Math.abs((previewData.total_debit || 0) - (previewData.total_credit || 0)).toLocaleString('fr-FR', { minimumFractionDigits: 2 }),
                                    currency,
                                })}
                            </span>
                        )
                    )}
                </div>

                {previewData && (
                    <div className="flex gap-4 text-xs font-semibold text-gray-800 bg-gray-50 px-4 py-2 rounded-lg border shadow-sm">
                        <div>
                            {t('accountingJournal.totalDebit')} :{' '}
                            <span className="text-blue-700 font-bold text-sm">
                                {formatAmount(previewData.total_debit)} {currency}
                            </span>
                        </div>
                        <div className="border-l pl-4">
                            {t('accountingJournal.totalCredit')} :{' '}
                            <span className="text-green-700 font-bold text-sm">
                                {formatAmount(previewData.total_credit)} {currency}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* État du mois, puis ce qui est à corriger avant clôture */}
            {previewData && previewData.pending_payslip_count > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-md text-xs flex items-center gap-2" data-cy="journal-pending">
                    <FaEye className="text-blue-600 text-base flex-shrink-0" />
                    <span>
                        {previewData.closed_move_count > 0
                            ? t('accountingJournal.partiallyClosed', { count: previewData.pending_payslip_count })
                            : t('accountingJournal.provisional', { count: previewData.pending_payslip_count })}
                    </span>
                </div>
            )}

            {previewData && previewData.missing_journal && (
                <div className="p-3 bg-red-50 border border-red-300 text-red-800 rounded-md text-xs font-medium flex items-center gap-2">
                    <FaExclamationTriangle className="text-red-600 text-base flex-shrink-0" />
                    <span>{t('accountingJournal.missingJournal')}</span>
                </div>
            )}

            {previewData && previewData.missing_adjustment_account && (
                <div className="p-3 bg-red-50 border border-red-300 text-red-800 rounded-md text-xs font-medium flex items-center gap-2">
                    <FaExclamationTriangle className="text-red-600 text-base flex-shrink-0" />
                    <span>{t('accountingJournal.adjustmentWarning')}</span>
                </div>
            )}

            {previewData && previewData.adjustment_total > 0 && !previewData.missing_adjustment_account && (
                <div className="p-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-md text-xs flex items-center gap-2" data-cy="journal-adjustment">
                    <FaExclamationTriangle className="text-amber-600 text-base flex-shrink-0" />
                    <span>
                        {t('accountingJournal.adjustmentNotice', {
                            amount: previewData.adjustment_total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })
                        })}
                    </span>
                </div>
            )}

            {previewData && previewData.unmapped_rules?.length > 0 && (
                <details className="p-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-md text-xs" data-cy="journal-unmapped">
                    <summary className="cursor-pointer font-medium">
                        {t('accountingJournal.unmappedWarning', { count: previewData.unmapped_rules.length })}
                    </summary>
                    <ul className="mt-2 divide-y divide-amber-200">
                        {previewData.unmapped_rules.map((rule) => (
                            <li key={rule.rule_id} className="flex justify-between gap-4 py-1.5">
                                <span>
                                    {rule.name} ({rule.code})
                                    {' · '}
                                    <a
                                        href={`/settings?tab=comptabilite&rule=${encodeURIComponent(rule.code)}`}
                                        className="underline text-amber-900 hover:text-amber-700"
                                    >
                                        {t('accountingJournal.configureRule')}
                                    </a>
                                </span>
                                <span className="font-medium whitespace-nowrap">
                                    {formatAmount(rule.amount)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {/* Gains et retenues dont la règle n'est pas cochée « ajouter à la comptabilité » (Yros/Support#63) */}
            {previewData && previewData.unflagged_rules?.length > 0 && (
                <details className="p-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-md text-xs" data-cy="journal-unflagged">
                    <summary className="cursor-pointer font-medium">
                        {t('accountingJournal.unflaggedWarning', { count: previewData.unflagged_rules.length })}
                    </summary>
                    <p className="mt-2">{t('accountingJournal.unflaggedHint')}</p>
                    <ul className="mt-2 divide-y divide-amber-200">
                        {previewData.unflagged_rules.map((rule) => (
                            <li key={rule.rule_id} className="flex justify-between gap-4 py-1.5">
                                <span>{rule.name} ({rule.code})</span>
                                <span className="font-medium whitespace-nowrap">{formatAmount(rule.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {previewData && previewData.external_contract_issues?.length > 0 && (
                <details className="p-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-md text-xs" data-cy="journal-external-issues">
                    <summary className="cursor-pointer font-medium">
                        {t('accountingJournal.externalIssues', { count: previewData.external_contract_issues.length })}
                    </summary>
                    <ul className="mt-2 divide-y divide-amber-200">
                        {previewData.external_contract_issues.map((issue) => (
                            <li key={issue.contract_id} className="flex justify-between gap-4 py-1.5">
                                <span>{issue.name}</span>
                                <span className="text-right">{issue.missing_accounts.join(', ')}</span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {/* Zone de Chargement / Erreur / Tableau */}
            {isPreviewLoading ? (
                <div className="flex flex-col items-center justify-center py-16 border rounded-lg bg-gray-50 shadow-inner">
                    <div className="w-9 h-9 border-3 border-[--color-primary] border-t-transparent rounded-full animate-spin mb-3"></div>
                    <span className="text-xs font-medium text-gray-600">{t('accountingJournal.previewLoading')}</span>
                </div>
            ) : previewError ? (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center gap-2">
                    <FaExclamationTriangle className="text-red-500 flex-shrink-0" size={16} />
                    <span>{previewError}</span>
                </div>
            ) : (
                <>
                <div className="flex flex-col md:flex-row md:items-center gap-3" data-cy="journal-table-tools">
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                        <input
                            type="search"
                            value={tableSearch}
                            onChange={(e) => { setTableSearch(e.target.value); setPage(1); }}
                            placeholder={t('accountingJournal.searchPlaceholder')}
                            className="w-full pl-8 pr-3 py-2 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[--color-primary]/30"
                            data-cy="journal-search"
                        />
                    </div>
                    {hasEmployeeColumns && (
                        <div className="relative md:w-64">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                            <input
                                type="search"
                                list="journal-employee-options"
                                value={employeeSearch}
                                onChange={(e) => { setEmployeeSearch(e.target.value); setPage(1); }}
                                placeholder={t('accountingJournal.employeeSearchPlaceholder')}
                                className="w-full pl-8 pr-3 py-2 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[--color-primary]/30"
                                data-cy="journal-employee-search"
                            />
                            <datalist id="journal-employee-options">
                                {employeeOptions.map((option) => (
                                    <option key={`${option.name}|${option.matricule}`} value={option.matricule || option.name}>
                                        {option.name}{option.matricule ? ` (${option.matricule})` : ''}
                                    </option>
                                ))}
                            </datalist>
                        </div>
                    )}
                    {accountIdx >= 0 && (
                        <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer whitespace-nowrap">
                            <input
                                type="checkbox"
                                checked={onlyMissingAccount}
                                onChange={(e) => { setOnlyMissingAccount(e.target.checked); setPage(1); }}
                                data-cy="journal-only-missing"
                            />
                            {t('accountingJournal.onlyMissingAccount')}
                        </label>
                    )}
                    <span className="text-xs text-gray-500 whitespace-nowrap" data-cy="journal-row-count">
                        {isFilteringTable
                            ? t('accountingJournal.filteredRows', { count: filteredRows.length, total: rows.filter((row) => !isTotalRow(row)).length })
                            : t('accountingJournal.rowCount', { count: rows.filter((row) => !isTotalRow(row)).length })}
                    </span>
                </div>
                <div className="border rounded-lg overflow-x-auto shadow-sm max-h-[60vh] overflow-y-auto bg-white">
                    <table className="min-w-full text-xs text-left">
                        <thead className="bg-gray-100 text-gray-700 font-semibold sticky top-0 border-b shadow-sm">
                            <tr>
                                {headers.map((h, i) => (
                                    <th key={i} className={`px-3.5 py-2.5 border-r last:border-r-0 whitespace-nowrap bg-gray-100 ${i === debitIdx || i === creditIdx ? 'text-right' : ''}`}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {pageRows.length === 0 ? (
                                <tr>
                                    <td colSpan={headers.length || 1} className="text-center py-12 text-gray-500" data-cy="journal-empty">
                                        {rows.length === 0
                                            ? (monthStatus ? t('accountingJournal.noDataToPreview') : t('accountingJournal.noPayslipForMonth'))
                                            : t('accountingJournal.noRowMatches')}
                                    </td>
                                </tr>
                            ) : (
                                pageRows.map((row, rIdx) => {
                                    const totalRow = isTotalRow(row);
                                    return (
                                        <tr key={rIdx} className={totalRow ? 'bg-gray-100 font-bold border-t border-b' : 'hover:bg-gray-50'}>
                                            {row.map((cell, cIdx) => {
                                                const isAccountCol = cIdx === accountIdx;
                                                const isAmountCol = cIdx === debitIdx || cIdx === creditIdx;
                                                const isMissingAccount = isAccountCol && !totalRow && !String(cell || '').trim();
                                                const hasAmount = isAmountCol && cell !== '' && cell !== null && cell !== undefined && !Number.isNaN(Number(cell));
                                                return (
                                                    <td
                                                        key={cIdx}
                                                        className={`px-3.5 py-2 border-r last:border-r-0 whitespace-nowrap ${
                                                            isMissingAccount ? 'bg-amber-100 text-amber-900 font-bold' : ''
                                                        } ${isAmountCol ? 'text-right tabular-nums' : ''}`}
                                                    >
                                                        {isMissingAccount ? (
                                                            <span className="italic text-amber-800">{t('accountingJournal.missingAccount')}</span>
                                                        ) : hasAmount ? (
                                                            Number(cell) === 0 ? '' : formatAmount(cell)
                                                        ) : (
                                                            cell
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                        {filteredRows.length > 0 && debitIdx >= 0 && creditIdx >= 0 && (
                            <tfoot className="sticky bottom-0 bg-gray-100 font-bold border-t-2 shadow-[0_-1px_2px_rgba(0,0,0,0.06)]" data-cy="journal-total">
                                <tr>
                                    {headers.map((_, i) => (
                                        <td key={i} className={`px-3.5 py-2.5 whitespace-nowrap ${i === debitIdx || i === creditIdx ? 'text-right tabular-nums' : ''}`}>
                                            {i === 0 ? (isFilteringTable ? t('accountingJournal.totalFiltered') : t('accountingJournal.totalGeneral')) : ''}
                                            {i === debitIdx ? formatAmount(filteredDebit) : ''}
                                            {i === creditIdx ? formatAmount(filteredCredit) : ''}
                                        </td>
                                    ))}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
                {pageCount > 1 && (
                    <div className="flex items-center justify-end gap-2 text-xs text-gray-700" data-cy="journal-pagination">
                        <button
                            type="button"
                            className="px-2.5 py-1 border rounded disabled:opacity-40"
                            onClick={() => setPage(currentPage - 1)}
                            disabled={currentPage <= 1}
                        >
                            {t('accountingJournal.previousPage')}
                        </button>
                        <span>{t('accountingJournal.pageOf', { page: currentPage, total: pageCount })}</span>
                        <button
                            type="button"
                            className="px-2.5 py-1 border rounded disabled:opacity-40"
                            onClick={() => setPage(currentPage + 1)}
                            disabled={currentPage >= pageCount}
                        >
                            {t('accountingJournal.nextPage')}
                        </button>
                    </div>
                )}
                </>
            )}
        </div>
    );

    if (isInline) {
        return (
            <div className="bg-white rounded-lg shadow-sm border p-4 space-y-4 w-full">
                {/* Header Barre d'actions Pleine Page */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-3 border-b gap-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            <FaFileExcel className="text-[--color-primary]" size={22} />
                            {t('accountingJournal.journalViewTitle')}
                        </h2>
                        <p className="text-xs text-gray-500">
                            {t('accountingJournal.pageSubtitle')}
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={handleFetchPreview}
                            disabled={isPreviewLoading}
                            className={`px-3.5 py-2 rounded font-medium text-xs flex items-center gap-1.5 border disabled:opacity-50 transition-all ${isStale ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border-amber-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                            data-cy="journal-refresh"
                        >
                            <FaSyncAlt className={isPreviewLoading ? "animate-spin text-indigo-600" : "text-gray-500"} size={13} />
                            {t('accountingJournal.refresh')}
                        </button>

                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowDownloadMenu((open) => !open)}
                                className="px-3.5 py-2 rounded bg-[--color-primary] hover:bg-[--bg-hover] text-white font-medium text-xs flex items-center gap-1.5 shadow transition-all"
                                data-cy="journal-download"
                            >
                                <FaDownload size={12} />
                                {t('accountingJournal.downloadMenu')}
                                <FaChevronDown size={10} />
                            </button>
                            {showDownloadMenu && (
                                <div className="absolute right-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-30 py-1" data-cy="journal-download-menu">
                                    {downloadChoices.map((choice) => (
                                        <button
                                            key={choice.id}
                                            type="button"
                                            onClick={choice.run}
                                            className="w-full px-3 py-2 text-left text-xs text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            {choice.icon}
                                            {choice.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {renderContent()}

                <Modal
                    isOpen={showProgressModal}
                    onClose={handleCloseProgressModal}
                    title={isExporting ? t('accountingJournal.exportingTitle') : exportDone ? t('accountingJournal.exportDoneTitle') : t('accountingJournal.exportErrorTitle')}
                    headerIcon={<FaFileExcel className="text-green-600" size={28} />}
                    subtitle={isExporting ? t('accountingJournal.generatingSubtitle', { format: downloadFormat.toUpperCase() }) : exportDone ? t('accountingJournal.exportDoneSubtitle') : ""}
                    showCloseButton={!isExporting}
                >
                    <div className="flex flex-col items-center justify-center py-8 px-6">
                        {isExporting ? (
                            <div className="w-10 h-10 border-4 border-green-300 border-t-green-600 rounded-full animate-spin mb-4"></div>
                        ) : exportDone ? (
                            <span className="text-lg font-semibold text-green-600">{t('accountingJournal.fileDownloaded')}</span>
                        ) : (
                            <span className="text-lg font-semibold text-red-600">{t('accountingJournal.exportErrorMessage')}</span>
                        )}
                    </div>
                </Modal>
            </div>
        );
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={t('accountingJournal.journalViewTitle')}
                headerIcon={<FaFileExcel className="text-[--color-primary]" size={28} />}
                subtitle={t('accountingJournal.pageSubtitle')}
                showCloseButton={true}
                maxWidth="max-w-5xl"
                footer={
                    <div className="flex justify-between items-center w-full mt-4 pt-3 border-t">
                        <button
                            className="px-3.5 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-xs flex items-center gap-2 transition-all border disabled:opacity-50"
                            onClick={handleFetchPreview}
                            disabled={isPreviewLoading}
                        >
                            <FaSyncAlt className={isPreviewLoading ? "animate-spin text-indigo-600" : "text-gray-500"} size={13} />
                            {t('accountingJournal.refresh')}
                        </button>
                        <div className="flex gap-3">
                            <button
                                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium text-sm"
                                onClick={onClose}
                            >
                                {t('basePrimeForm.cancel')}
                            </button>
                            <button
                                className="px-4 py-2 rounded bg-[--color-primary] hover:bg-[--color-orange] text-white font-medium text-sm flex items-center gap-2 transition-all shadow"
                                onClick={() => handleDownloadJournal(fileFormat)}
                            >
                                <FaDownload size={14} />
                                {t('accountingJournal.download', { format: fileFormat.toUpperCase() })}
                            </button>
                        </div>
                    </div>
                }
            >
                {renderContent()}
            </Modal>

            <Modal
                isOpen={showProgressModal}
                onClose={handleCloseProgressModal}
                title={isExporting ? t('accountingJournal.exportingTitle') : exportDone ? t('accountingJournal.exportDoneTitle') : t('accountingJournal.exportErrorTitle')}
                headerIcon={<FaFileExcel className="text-green-600" size={28} />}
                subtitle={isExporting ? t('accountingJournal.generatingSubtitle', { format: downloadFormat.toUpperCase() }) : exportDone ? t('accountingJournal.exportDoneSubtitle') : ""}
                showCloseButton={!isExporting}
            >
                <div className="flex flex-col items-center justify-center py-8 px-6">
                    {isExporting ? (
                        <div className="w-10 h-10 border-4 border-green-300 border-t-green-600 rounded-full animate-spin mb-4"></div>
                    ) : exportDone ? (
                        <span className="text-lg font-semibold text-green-600">{t('accountingJournal.fileDownloaded')}</span>
                    ) : (
                        <span className="text-lg font-semibold text-red-600">{t('accountingJournal.exportErrorMessage')}</span>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default AccountingJournal;
